import { GetterTree, MutationTree, ActionTree, Module } from "vuex";
import { RootState } from "..";
import { IPropertyPerformanceState } from "../states/property-performance-state";
import container from "../../inversify.config";
import PerformanceService from "../../services/performance.service";
import EnquiryService from "../../services/enquiry.service";
import { IStringPerformanceSummary } from "../../models/interfaces/performance/string-performance-summary.model.interface";
import { IDailyPerformanceSummary } from "../../models/interfaces/performance/daily-performance-summary.model.interface";
import { IUrlPerformance } from "../../models/interfaces/performance/url-performance.model.interface";
import { IDetailedEnquirySummary } from "@/models/interfaces/enquiry/detailed-enquiry-summary.model.interface";
import { DetailedEnquirySummary } from "@/models/enquiry/detailed-enquiry-summary.model";
import { IContentTemplatePerformanceSummary } from "@/models/interfaces/performance/content-template-performance-summary.model.interface";
import { IZonePerformanceSummary } from "@/models/interfaces/performance/zone-performance-summary.model.interface";
import { EnquirySource } from '@/models//enquiry/enquiry-source.model';

export const state: IPropertyPerformanceState = {
  zonePerformance: [],
  zoneQualityArrivalPerformance: [],
  urlPerformance: [],
  templatePerformance: [],
  zoneDevicePerformance: new Map<string, IStringPerformanceSummary[]>(),
  zoneDeviceQualityArrivalPerformance: new Map<string, IStringPerformanceSummary[]>(),
  dailyPerformance : {
    keys: [],
    loads: [],
    impressions: [],
    clicks: [],
    keyPresses: []
  },
  enquiryPerformance: new DetailedEnquirySummary(),
  enquiryPerformanceBySource: []
};

export const getters: GetterTree<IPropertyPerformanceState, RootState> = {
  propertyZonePerformance(state) {
    return state.zonePerformance;
  },
  propertyZoneQualityArrivalPerformance(state) {
    return state.zoneQualityArrivalPerformance;
  },
  propertyZoneDevicePerformance(state) {
    return state.zoneDevicePerformance;
  },
  propertyZoneDeviceQualityArrivalPerformance(state) {
    return state.zoneDeviceQualityArrivalPerformance;
  },
  propertyUrlPerformance(state) {
    return state.urlPerformance;
  },
  propertyTemplatePerformance(state) {
    return state.templatePerformance;
  },
  propertyDailyPerformance(state) {
    return state.dailyPerformance;
  },
  propertyEnquiryPerformance(state) {
    return state.enquiryPerformance;
  },
  propertyEnquiryPerformanceBySource(state) {
    return state.enquiryPerformanceBySource;
  },
  zoneLoadsCount(state) {
    return state.zonePerformance.map((x) => x.loads).reduce((x, y) => x + y, 0);
  },
  zoneImpressionCount(state) {
    return state.zonePerformance.map((x) => x.impressions).reduce((x, y) => x + y, 0);
  },
  zoneClicksCount(state) {
    return state.zonePerformance.map((x) => x.clicks).reduce((x, y) => x + y, 0);
  },
  zoneKeypressCount(state) {
    return state.zonePerformance.map((x) => x.keyPresses).reduce((x, y) => x + y, 0);
  },
  urlLoadsCount(state) {
    return state.urlPerformance.map((x) => x.loads).reduce((x, y) => x + y, 0);
  },
  urlImpressionCount(state) {
    return state.urlPerformance.map((x) => x.impressions).reduce((x, y) => x + y, 0);
  },
  urlClicksCount(state) {
    return state.urlPerformance.map((x) => x.clicks).reduce((x, y) => x + y, 0);
  }
};

export const mutations: MutationTree<IPropertyPerformanceState> = {
  propertyZonePerformance(state, zonePerformance: IZonePerformanceSummary[]) {
    state.zonePerformance = zonePerformance;
  },
  propertyZoneQualityArrivalPerformance(state, zoneQualityArrivalPerformance: IZonePerformanceSummary[]) {
    state.zoneQualityArrivalPerformance = zoneQualityArrivalPerformance;
  },
  propertyUrlPerformance(state, data: IStringPerformanceSummary[]) {
    state.urlPerformance = data;
  },
  propertyTemplatePerformance(state, data: IContentTemplatePerformanceSummary[]) {
    state.templatePerformance = data;
  },
  resetPropertyZonePerformance(state) {
    state.zonePerformance = [];
  },
  resetPropertyZoneDevicePerformance(state) {
    state.zonePerformance = [];
  },
  resetPropertyZoneQualityArrivalPerformance(state) {
    state.zoneQualityArrivalPerformance = [];
  },
  resetPropertyZoneDeviceQualityArrivalPerformance(state) {
    state.zoneDeviceQualityArrivalPerformance.clear();
  },
  propertyDailyPerformance(state, data: IDailyPerformanceSummary) {
    state.dailyPerformance = data;
  },
  propertyEnquiryPerformance(state, data: IDetailedEnquirySummary) {
    if (data) {
      state.enquiryPerformance = data;
    } else {
      state.enquiryPerformance = new DetailedEnquirySummary();
    }
  },
  propertyEnquiryPerformanceBySource(state, data: IDetailedEnquirySummary[]) {
    state.enquiryPerformanceBySource = data;
  },
  propertyZoneDevicePerformance(state, data: Map<string, IStringPerformanceSummary[]>) {
    state.zoneDevicePerformance = data;
  },
  propertyZoneDeviceQualityArrivalPerformance(state, data: Map<string, IStringPerformanceSummary[]>) {
    state.zoneDeviceQualityArrivalPerformance = data;
  }
};

export const actions: ActionTree<IPropertyPerformanceState, RootState> = {
  async retrievePropertyZonePerformance({ commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getPropertyZonePerformance(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          if (value.result && value.result.length > 0) {
              commit("propertyZonePerformance", value.result);
          } else {
            commit("resetPropertyZonePerformance");
          }
          resolve(true);
        });
    });
  },
  async retrievePropertyZoneQualityArrivalPerformance({commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getPropertyZonePerformance(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo,
          2)
        .then((value) => {
          if (value.result && value.result.length > 0) {
              commit("propertyZoneQualityArrivalPerformance", value.result);
          } else {
            commit("resetPropertyZoneQualityArrivalPerformance");
          }
          resolve(true);
        });
    });
  },
  async retrievePropertyZoneDeviceQualityArrivalPerformance({ commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getZoneDeviceSummary(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo,
          2)
        .then((value) => {
          if (value.result) {
              commit("propertyZoneDeviceQualityArrivalPerformance", value.result);
          }
          resolve(true);
        });
    });
  },
  async retrievePropertyZoneDevicePerformance({ commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getZoneDeviceSummary(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          if (value.result) {
              commit("propertyZoneDevicePerformance", value.result);
          }
          resolve(true);
        });
    });
  },
  async retrievePropertyUrlPerformance({ commit, rootState }, model: IUrlPerformance) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getPropertyUrlPerformance(
          model.propertyId,
          model.path,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo,
          model.size)
        .then((value) => {
          commit("propertyUrlPerformance", value.result);
          resolve(true);
        });
    });
  },
  async retrievePropertyTemplatePerformance({ commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getPropertyTemplatePerformance(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          commit("propertyTemplatePerformance", value.result);
          resolve(true);
        });
    });
  },
  async retrievePropertyDailyPerformance({ commit, rootState }, propertyId: number) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getDailyPropertyPerformance(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          commit("propertyDailyPerformance", value);
          resolve(true);
        });
    });
  },
  async retrievePropertyEnquiryPerformance({ commit, rootState, rootGetters }, propertyId: number) {
    return new Promise((resolve, reject) => {
      if (rootGetters["userModule/revenueAccess"]) {
      container.get<EnquiryService>(EnquiryService)
        .getPropertyEnquiries(
          propertyId,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          if (!rootGetters["userModule/isAdmin"] && value.result) {
            if (propertyId === 19 || propertyId === 20) {
              value.result.revenue = value.result.revenue * 0.30;
            } else {
              value.result.revenue = value.result.revenue * 0.25;
            }
          }
          commit("propertyEnquiryPerformance", value.result);
          resolve(true);
        });
      } else {
        resolve(true);
      }
    });
  },
  async retrieveEnquirySummaryBySource({ commit, rootState, rootGetters }, enquirySource: EnquirySource) {
    return new Promise((resolve, reject) => {
      if (rootGetters["userModule/revenueAccess"]) {
      container.get<EnquiryService>(EnquiryService)
        .getEnquirySummaryBySource(
          enquirySource,
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
            if (!rootGetters["userModule/isAdmin"] && value.result) {
              if (enquirySource.propertyId === 19 || enquirySource.propertyId === 20) {
                value.result[0].revenue = value.result[0].revenue * 0.30;
              } else {
                value.result[0].revenue = value.result[0].revenue * 0.25;
              }
          }
          if(value.result) {
            commit("propertyEnquiryPerformanceBySource", value.result[0]);
          }
          resolve(true);
        });
      } else {
        resolve(true);
      }
    });
  }
};

export const propertyPerformanceModule: Module<IPropertyPerformanceState, RootState> = {
  state, getters, mutations, actions, namespaced: true
};
